import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";

export default function Account() {
  return (
    <div className="bg-ink min-h-screen" data-testid="account-page">
      <Seo title="Customer Account — Dirty Church Chronicles" description="Customer accounts for the Dirty Church Chronicles Official Store." path="/account" noindex />
      <section className="pt-40 pb-24 max-w-3xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Customer Account</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-5xl tracking-tight mb-6">
            Accounts Open at Launch
          </h1>
          <p className="font-reading text-xl text-paper/70 leading-relaxed mb-12">
            When the Official Store launches, your account will hold order history, saved addresses, shipment
            tracking, and support access — with guest checkout always available.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="border border-paper/10 bg-coal p-10 text-center">
            <LogoMark size={44} className="mx-auto mb-6" />
            <p className="font-ui text-sm text-paper/55 mb-8 max-w-sm mx-auto">
              Until then, the reader list carries every announcement — including the day the store opens.
            </p>
            <Link to="/connect" data-testid="account-join-link"
              className="inline-block font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-8 py-4 rounded-full transition-colors duration-300 hover:bg-crimsonhi">
              Join the Reader List
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

export function OrderStatus() {
  const [form, setForm] = useState({ order: "", email: "" });
  const [checked, setChecked] = useState(false);

  return (
    <div className="bg-ink min-h-screen" data-testid="order-status-page">
      <Seo title="Order Status — Dirty Church Chronicles" description="Check the status of a Dirty Church Chronicles order." path="/order-status" noindex />
      <section className="pt-40 pb-24 max-w-2xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Order Status</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-5xl tracking-tight mb-6">
            Track an Order
          </h1>
          <p className="font-reading text-xl text-paper/70 leading-relaxed mb-12">
            Order tracking activates when the Official Store launches. Kindle purchases made on Amazon are tracked
            in your Amazon account.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          {checked ? (
            <div className="border border-gold/30 p-8" data-testid="order-status-result">
              <p className="font-display text-xl text-gold">The store has not opened yet.</p>
              <p className="font-ui text-sm text-paper/55 mt-3">
                No orders exist before launch. If you purchased the Kindle edition, tracking lives in your Amazon
                account. Questions? Use the <Link to="/connect" className="text-crimsonhi underline underline-offset-4">contact page</Link>.
              </p>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setChecked(true); }} className="space-y-4" data-testid="order-status-form">
              <input required value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
                placeholder="Order number" aria-label="Order number" data-testid="order-number-input"
                className="w-full bg-transparent border border-paper/20 focus:border-crimson px-4 py-3.5 font-ui text-sm text-paper placeholder:text-paper/30 outline-none transition-colors duration-300" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email used at checkout" aria-label="Email used at checkout" data-testid="order-email-input"
                className="w-full bg-transparent border border-paper/20 focus:border-crimson px-4 py-3.5 font-ui text-sm text-paper placeholder:text-paper/30 outline-none transition-colors duration-300" />
              <button type="submit" data-testid="order-status-btn"
                className="font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-paper text-ink px-8 py-4 rounded-full transition-colors duration-300 hover:bg-white">
                Check Status
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
